import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, Lock, User, Mail, Smile, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const LoginPage = () => {
    const { user, login, loginWithGoogle, loading } = useAuth();
    const location = useLocation();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(location.state?.error || '');
    const [isLoading, setIsLoading] = useState(false);
    const [systemSettings, setSystemSettings] = useState(null);
    const googleButtonRef = useRef(null);

    // Passwort vergessen (Reset-Link per E-Mail)
    const [forgotOpen, setForgotOpen] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);

    const handleForgotSubmit = async (e) => {
        e.preventDefault();
        if (!forgotEmail.trim()) return;
        setForgotLoading(true);
        try {
            await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail.trim() });
        } catch (err) {
            // Antwort ist absichtlich immer gleich - auch bei Fehlern nichts verraten
        }
        setForgotLoading(false);
        setForgotSent(true);
    };

    // Social-Face-Login (Magic-Link)
    const [sfEmail, setSfEmail] = useState('');
    const [sfLoading, setSfLoading] = useState(false);
    const [sfSent, setSfSent] = useState(false);
    const [sfError, setSfError] = useState('');

    const handleSocialFaceSubmit = async (e) => {
        e.preventDefault();
        setSfError('');
        if (!sfEmail.trim()) {
            setSfError('Bitte deine E-Mail-Adresse eingeben.');
            return;
        }
        setSfLoading(true);
        try {
            await axios.post(`${API_URL}/api/sf/auth/magic-link`, { email: sfEmail.trim(), phone: '' });
            // Login nur per E-Mail (Nummern-Feld entfernt: ohne SMS-Dienst kaeme ohnehin nur eine Mail)
            // Antwort ist bewusst generisch (kein Konto-Leak) - immer Erfolg anzeigen
            setSfSent(true);
        } catch (err) {
            setSfError(err.response?.data?.detail || 'Anmeldung fehlgeschlagen. Bitte später erneut versuchen.');
        }
        setSfLoading(false);
    };

    useEffect(() => {
        // Fetch system settings for logo (public endpoint, no auth needed)
        const fetchSettings = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/system/public-settings`);
                setSystemSettings(response.data);
            } catch (error) {
                console.log('Using default settings');
            }
        };
        fetchSettings();
    }, []);

    const handleGoogleCredential = useCallback(async (response) => {
        setError('');
        const result = await loginWithGoogle(response.credential);
        if (!result.success) {
            setError(result.error);
        }
    }, [loginWithGoogle]);

    useEffect(() => {
        // Load Google Identity Services and render the sign-in button
        if (!GOOGLE_CLIENT_ID) return;

        const renderButton = () => {
            if (window.google?.accounts?.id && googleButtonRef.current) {
                window.google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: handleGoogleCredential
                });
                window.google.accounts.id.renderButton(googleButtonRef.current, {
                    theme: 'outline',
                    size: 'large',
                    width: 368,
                    text: 'signin_with',
                    locale: 'de'
                });
            }
        };

        if (window.google?.accounts?.id) {
            renderButton();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = renderButton;
        document.head.appendChild(script);
    }, [handleGoogleCredential]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (user) {
        // Redirect influencers to benefits page
        if (user.role === 'influencer') {
            return <Navigate to="/benefits" replace />;
        }
        return <Navigate to="/" replace />;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        const result = await login(username, password);
        
        if (!result.success) {
            setError(result.error);
        }
        
        setIsLoading(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            {/* Background decoration */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-primary/5 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-primary/5 rounded-full blur-3xl" />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-4xl relative"
            >
                {/* Logo */}
                <div className="text-center mb-8">
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.1, type: 'spring', bounce: 0.5 }}
                        className={`inline-flex w-20 h-20 rounded-2xl items-center justify-center mb-4 shadow-floating overflow-hidden ${!systemSettings?.logo_url ? 'bg-primary' : ''}`}
                    >
                        {systemSettings?.logo_url ? (
                            <img 
                                src={systemSettings.logo_url} 
                                alt="Logo" 
                                className="w-full h-full object-contain"
                            />
                        ) : (
                            <span className="text-white font-bold text-2xl">A</span>
                        )}
                    </motion.div>
                    <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {systemSettings?.company_name || 'AgencyOS'}
                    </h1>
                    <p className="text-muted-foreground mt-1">AgencyOS</p>
                    <p className="text-sm text-muted-foreground mt-3 max-w-lg mx-auto">
                        Interne Arbeitsplattform für Team, Kundenbetreuung und Social Faces —
                        mit Aufgaben- und Projektverwaltung, Zeiterfassung, Kalender, Content-Planung und Abrechnung.
                        Der Zugang ist autorisierten Mitarbeitenden und beauftragten Talenten vorbehalten.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch">
                <Card className="shadow-floating border-border/40">
                    <CardHeader className="space-y-1 pb-4">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-sm font-bold tracking-wide mb-2 w-fit">
                            <User className="w-4 h-4" /> TEAM
                        </div>
                        <CardTitle className="text-2xl">Anmelden</CardTitle>
                        <CardDescription>
                            Für Mitarbeiter — mit Benutzername & Passwort
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="p-3 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive text-sm"
                                    data-testid="login-error"
                                >
                                    {error}
                                </motion.div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="username">Benutzername</Label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="username"
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder="Benutzername eingeben"
                                        className="pl-10 h-12 rounded-xl"
                                        required
                                        data-testid="username-input"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">Passwort</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Passwort eingeben"
                                        className="pl-10 pr-10 h-12 rounded-xl"
                                        required
                                        data-testid="password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => { setForgotOpen(!forgotOpen); setForgotSent(false); setForgotEmail(username.includes('@') ? username : ''); }}
                                        className="text-xs text-muted-foreground hover:text-primary transition-colors"
                                        data-testid="forgot-password-link"
                                    >
                                        Passwort vergessen?
                                    </button>
                                </div>
                            </div>

                            {forgotOpen && (
                                <motion.div
                                    initial={{ opacity: 0, y: -6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="p-3 rounded-xl bg-muted/50 border border-border/60 space-y-2"
                                    data-testid="forgot-password-box"
                                >
                                    {forgotSent ? (
                                        <p className="text-sm text-muted-foreground flex items-start gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                            Wenn ein Konto mit dieser Adresse existiert, ist ein Link zum Zurücksetzen unterwegs. Der Link gilt 24 Stunden. Bitte auch den Spam-Ordner prüfen.
                                        </p>
                                    ) : (
                                        <>
                                            <p className="text-xs text-muted-foreground">Gib deine E-Mail-Adresse ein, wir schicken dir einen Link zum Zurücksetzen.</p>
                                            <div className="flex gap-2">
                                                <Input
                                                    type="email"
                                                    value={forgotEmail}
                                                    onChange={(e) => setForgotEmail(e.target.value)}
                                                    placeholder="E-Mail-Adresse"
                                                    className="h-10 rounded-lg"
                                                    data-testid="forgot-password-email"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="secondary"
                                                    className="h-10 rounded-lg"
                                                    onClick={handleForgotSubmit}
                                                    disabled={forgotLoading || !forgotEmail.trim()}
                                                    data-testid="forgot-password-submit"
                                                >
                                                    {forgotLoading ? '...' : 'Link senden'}
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                </motion.div>
                            )}

                            <Button
                                type="submit"
                                className="w-full h-12 rounded-xl text-base font-medium"
                                disabled={isLoading}
                                data-testid="login-submit"
                            >
                                {isLoading ? (
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    'Anmelden'
                                )}
                            </Button>
                        </form>

                        {GOOGLE_CLIENT_ID && (
                            <>
                                {/* Divider */}
                                <div className="relative my-4">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-border" />
                                    </div>
                                    <div className="relative flex justify-center text-xs">
                                        <span className="bg-card px-3 text-muted-foreground">oder</span>
                                    </div>
                                </div>

                                {/* Google Sign-In (Google Identity Services) */}
                                <div
                                    ref={googleButtonRef}
                                    className="flex justify-center"
                                    data-testid="google-login-btn"
                                />
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Social-Face-Login (Magic-Link, ohne Passwort) - blau abgehoben */}
                <Card className="shadow-floating border-primary/30 bg-primary text-primary-foreground overflow-hidden relative">
                    <div className="absolute -top-16 -right-16 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none" />
                    <CardHeader className="space-y-1 pb-4 relative">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 text-sm font-bold tracking-wide mb-2 w-fit">
                            <Smile className="w-4 h-4" /> SOCIAL FACE
                        </div>
                        <CardTitle className="text-2xl text-primary-foreground">Login per Link</CardTitle>
                        <CardDescription className="text-primary-foreground/80">
                            Kein Passwort — wir schicken dir einen Anmelde-Link per E-Mail
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="relative">
                        {sfSent ? (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.96 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="text-center py-6"
                            >
                                <CheckCircle2 className="w-12 h-12 text-primary-foreground mx-auto mb-3" />
                                <p className="font-semibold mb-1">E-Mail unterwegs</p>
                                <p className="text-sm text-primary-foreground/80">
                                    Falls ein Konto mit dieser E-Mail-Adresse existiert, findest du den Anmelde-Link in deinem Postfach.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => { setSfSent(false); setSfEmail('');  }}
                                    className="mt-4 text-sm font-medium text-primary-foreground underline underline-offset-4 hover:opacity-80"
                                >
                                    Erneut senden
                                </button>
                            </motion.div>
                        ) : (
                            <form onSubmit={handleSocialFaceSubmit} className="space-y-4">
                                {sfError && (
                                    <div className="p-3 rounded-xl bg-white/15 border border-white/30 text-primary-foreground text-sm">
                                        {sfError}
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <Label htmlFor="sf-email" className="text-primary-foreground">E-Mail</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-foreground/70" />
                                        <Input
                                            id="sf-email"
                                            type="email"
                                            value={sfEmail}
                                            onChange={(e) => setSfEmail(e.target.value)}
                                            placeholder="deine@email.at"
                                            className="pl-10 h-12 rounded-xl bg-white/15 border-white/30 text-primary-foreground placeholder:text-primary-foreground/50 focus-visible:ring-white/50"
                                            required
                                            data-testid="sf-email-input"
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-primary-foreground/70 leading-relaxed">
                                    Bitte genau die E-Mail-Adresse verwenden, die du bei deiner Bewerbung angegeben hast. Der Anmelde-Link kommt an dieses Postfach.
                                </p>
                                <Button
                                    type="submit"
                                    className="w-full h-12 rounded-xl text-base font-semibold gap-2 bg-white text-primary hover:bg-white/90"
                                    disabled={sfLoading}
                                    data-testid="sf-login-submit"
                                >
                                    {sfLoading ? (
                                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <>Anmelde-Link senden</>
                                    )}
                                </Button>
                                <p className="text-xs text-primary-foreground/70 text-center">
                                    Für Models & Talente auf Honorarbasis
                                </p>
                            </form>
                        )}
                    </CardContent>
                </Card>
                </div>

                <p className="text-center text-sm text-muted-foreground mt-6">
                    Probleme beim Anmelden? Kontaktiere deinen Administrator.
                </p>
                <p className="text-center text-xs text-muted-foreground mt-2">
                    <a href="/datenschutz" className="hover:text-foreground">Datenschutz</a>
                    <span className="mx-2">·</span>
                    <a href="/nutzungsbedingungen" className="hover:text-foreground">Nutzungsbedingungen</a>
                </p>
            </motion.div>
        </div>
    );
};

export default LoginPage;
